import React, { useEffect, useState } from 'react';
import EmployeeProcessService from "../services/EmployeeProcessService";
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { useLoading } from '../Utils/LoadingContext';
import CustomDataTable from '../components/CustomDataTable';
import '../components/Common.css';

const DailyPunch = () => {
  const empcode = localStorage.getItem('empcode');
  const { setLoading } = useLoading();

  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    CompanyName: '',
    PlantName: '',
    VenderName: '',
    DepartmentName: '',
    EmployeeCode: '',
    EmployeeName: '',
    DoJStart: '',
    DoJEnd: '',
    Grade: ''
  });

  const [departments, setDepartments] = useState([]);
  const [plant, setPlant] = useState([]);
  const [company, setCompany] = useState([]);
  const [vender, setVender] = useState([]);
  const [grade, setGrade] = useState([]);

  // 🔹 Toggles
  const [showFilters, setShowFilters] = useState(true);
  const [showTable, setShowTable] = useState(true);

  useEffect(() => {
    loadDropdowns();
  }, []);

  const loadDropdowns = async () => {
    try {
      const [deptRes, plantRes, companyRes, venderRes, gradeRes] = await Promise.all([
        EmployeeProcessService.getDepartment(empcode),
        EmployeeProcessService.getPlant(empcode),
        EmployeeProcessService.getCompany("User"),
        EmployeeProcessService.getVender("User"),
        EmployeeProcessService.getGrades()
      ]);
      if (deptRes?.data?.statusCode === 200) setDepartments(deptRes.data.result);
      if (plantRes?.data?.statusCode === 200) setPlant(plantRes.data.result);
      if (companyRes?.data?.statusCode === 200) setCompany(companyRes.data.result);
      if (venderRes?.data?.statusCode === 200) setVender(venderRes.data.result);
      if (gradeRes?.data?.statusCode === 200) setGrade(gradeRes.data.result);
    } catch (error) {
      console.error("Error loading dropdowns:", error);
    }
  };

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date)) return value;
    return date.toLocaleDateString("en-GB");
  };

  const handlePlantChange = async (e) => {
    const plantId = e.target.value;
    setFilters(prev => ({ ...prev, PlantName: plantId, DepartmentName: "" }));
    setDepartments([]);

    if (!plantId) return;

    try {
      const res = await EmployeeProcessService.getDepartment(empcode, plantId);
      if (res?.data?.statusCode === 200) {
        setDepartments(res.data.result);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      console.error("Error loading departments:", error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await EmployeeProcessService.getDailyPunch(filters);
      if (response.data.statusCode === 200 && response.data.result.length > 0) {
        setData(response.data.result);
      } else {
        setData([]);
        toast.info("No record found");
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (filters.CompanyName === "") {
      alert("Please select Company.");
      return;
    }
    // if (filters.PlantName === "") {
    //   alert("Please select Plant.");
    //   return;
    // }
    if (!filters.DoJStart) {
      alert("Please select date.");
      return;
    }
    fetchData();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const transformDataForExcel = (data) => {
    return data.map(row => {
      const newRow = {};
      Object.keys(row).forEach(key => {
        if (row[key] && /^\d{4}-\d{2}-\d{2}T/.test(row[key])) {
          newRow[key] = formatDate(row[key]);
        } else {
          newRow[key] = row[key];
        }
      });
      return newRow;
    });
  };

  const downloadExcel = () => {
    const formattedData = transformDataForExcel(data);
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DailyPunch");
    XLSX.writeFile(workbook, "DailyPunch.xlsx");
  };

  return (
    <div className="container mt-3">

      {/* 🔹 Heading */}
      <div className="mb-2 p-2 bg-light rounded">
        <h6 className="m-0 text-primary">Daily Punch Report</h6>
      </div>

      {/* 🔹 Filters Toggle */}
      <div
        className="d-flex justify-content-between align-items-center mb-2 p-2 bg-secondary bg-opacity-10 rounded"
        style={{ cursor: "pointer" }}
        onClick={() => setShowFilters(!showFilters)}
      >
        <h6 className="m-0">Filters</h6>
        <span style={{ fontSize: "12px" }}>{showFilters ? "▲" : "▼"}</span>
      </div>

      {showFilters && (
        <div className="row filter-section mb-3">
          <div className="col-md-3">
            <label className="text-primary">Select Company</label>
            <select name="CompanyName" value={filters.CompanyName} onChange={handleChange} className="form-select filter-input">
              <option value="">Select Company</option>
              {company.map(comp => (
                <option key={comp.id} value={comp.id}>{comp.name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="text-primary">Select Plant</label>
            <select name="PlantName" value={filters.PlantName} onChange={handlePlantChange} className="form-select filter-input">
              <option value="">Select Plant</option>
              {plant.map(plan => (
                <option key={plan.id} value={plan.id}>{plan.name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="text-primary">Select Vendor</label>
            <select name="VenderName" value={filters.VenderName} onChange={handleChange} className="form-select filter-input">
              <option value="">Select Vendor</option>
              {vender.map(ven => (
                <option key={ven.id} value={ven.id}>{ven.name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="text-primary">Select Department</label>
            <select name="DepartmentName" value={filters.DepartmentName} onChange={handleChange} className="form-select filter-input">
              <option value="">Select Department</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-3 mt-2">
            <label className="text-primary">Date</label>
            <input type="date" name="DoJStart" value={filters.DoJStart} onChange={handleChange} className="form-control filter-input" />
          </div>

          <div className="col-md-3 mt-2">
            <label className="text-primary">Employee Code</label>
            <input name="EmployeeCode" value={filters.EmployeeCode} onChange={handleChange} className="form-control filter-input" />
          </div>

          <div className="col-md-3 mt-2">
            <label className="text-primary">Employee Name</label>
            <input name="EmployeeName" value={filters.EmployeeName} onChange={handleChange} className="form-control filter-input" />
          </div>

          <div className="col-md-3 mt-2">
            <label className="text-primary">Select Skill</label>
            <select name="Grade" value={filters.Grade} onChange={handleChange} className="form-select filter-input">
              <option value="">Select Skill</option>
              {grade.map(grd => (
                <option key={grd.id} value={grd.name}>{grd.name}</option>
              ))}
            </select>
          </div>

          <div className="col-12 mt-3 d-flex gap-2">
            <button className="btn btn-primary" onClick={applyFilters}>Search</button>
            {data.length > 0 && (
              <button className="btn btn-success" onClick={downloadExcel}>Download Excel</button>
            )}
          </div>
        </div>
      )}

      {/* 🔹 Data Toggle */}
      <div
        className="d-flex justify-content-between align-items-center mb-2 p-2 bg-secondary bg-opacity-10 rounded"
        style={{ cursor: "pointer" }}
        onClick={() => setShowTable(!showTable)}
      >
        <h6 className="m-0">Data</h6>
        <span style={{ fontSize: "12px" }}>{showTable ? "▲" : "▼"}</span>
      </div>

      {showTable && (
        <div className="table-responsive">
          <CustomDataTable data={data} title="Employee List" />
        </div>
      )}
    </div>
  );
};

export default DailyPunch;
